import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ChevronLeft, GitBranch, Search, X } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchFeatureFlags, fetchFlagDeps, setFlagDeps, type AdminFeatureFlag } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/UiProviders'
import { PermissionDenied } from '@/components/States'
import { cn } from '@/lib/cn'

/**
 * A14.07 — Flag bog'liqliklari (oldindan-shartlar). Flag FAQAT barcha shart-flaglari
 * yoqilганда yoqiladi. Server (`GET /settings/features`) fixpoint-gate qo'llaydi:
 * transitiv zanjir kaskad bo'ladi (A→B→C), sikl xavfsiz. O'z-o'ziga bog'liqlik rad
 * etiladi. Yozuv `POST /admin/settings/flags/deps` (feature_-only, audited).
 * AUDIT_VIEW-gated.
 */
export function FlagDependencies() {
  const t = useT()
  const { can } = useAuth()
  const { toast } = useToast()
  const [params, setParams] = useSearchParams()
  const selected = params.get('key') ?? ''
  const [flags, setFlags] = useState<AdminFeatureFlag[] | null>(null)
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [q, setQ] = useState('')
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    fetchFeatureFlags({ filters: [], sort: [], page: 1, pageSize: 500, search: '', signal: new AbortController().signal })
      .then((r) => setFlags(r.rows))
      .catch(() => setFlags([]))
  }, [])

  const loadDeps = useCallback((key: string) => {
    if (!key) {
      setPicked(new Set())
      return
    }
    setLoading(true)
    fetchFlagDeps(key)
      .then((deps) => setPicked(new Set(deps)))
      .catch(() => setPicked(new Set()))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    loadDeps(selected)
  }, [selected, loadDeps])

  const selectKey = (key: string) => setParams(key ? { key } : {}, { replace: true })

  const toggle = (key: string) =>
    setPicked((s) => {
      const n = new Set(s)
      n.has(key) ? n.delete(key) : n.add(key)
      return n
    })

  // Candidate prerequisites = every OTHER flag (a flag can't depend on itself).
  const candidates = useMemo(() => {
    const s = q.trim().toLowerCase()
    return (flags ?? [])
      .filter((f) => f.key !== selected)
      .filter((f) => !s || f.key.toLowerCase().includes(s))
  }, [flags, selected, q])

  const save = () => {
    if (!selected || busy) return
    setBusy(true)
    setFlagDeps(selected, [...picked])
      .then(() => toast(picked.size === 0 ? t.flags.depsCleared : t.flags.depsSaved, { tone: 'success' }))
      .catch((e: unknown) => toast(t.flags.toggleFailed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) }))
      .finally(() => setBusy(false))
  }

  if (!can(PERMISSIONS.AUDIT_VIEW)) return <PermissionDenied permission={PERMISSIONS.AUDIT_VIEW} />

  return (
    <>
      <PageHeader
        title={t.flags.depsTitle}
        screenId="A14.07"
        actions={
          <Link
            to="/flags"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.flags.title}
          </Link>
        }
      />
      <div className="border-b border-border-subtle p-3">
        <label className="mb-1 block text-xs text-tertiary">{t.flags.key}</label>
        <select
          value={selected}
          onChange={(e) => selectKey(e.target.value)}
          className="w-full max-w-md rounded-md border border-border-default bg-surface-2 px-2.5 py-1.5 font-mono text-sm text-primary outline-none"
        >
          <option value="">— {t.flags.pickFlag} —</option>
          {(flags ?? []).map((f) => (
            <option key={f.key} value={f.key}>{f.key}</option>
          ))}
        </select>
      </div>

      {!selected ? (
        <div className="flex-1 px-3 py-10 text-center text-sm text-tertiary">
          <GitBranch size={24} className="mx-auto mb-2 opacity-60" />
          {t.flags.pickFlag}
        </div>
      ) : loading ? (
        <div className="flex-1 p-6 text-center text-sm text-tertiary">…</div>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto p-3">
          <div className="max-w-2xl space-y-4">
            <p className={cn('rounded-md px-3 py-2 text-xs', picked.size === 0 ? 'bg-surface-2 text-tertiary' : 'bg-accent-subtle text-accent')}>
              {picked.size === 0 ? t.flags.depsNoneNote : t.flags.depsActiveNote(selected)}
            </p>

            {picked.size > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {[...picked].map((key) => (
                  <button
                    key={key}
                    onClick={() => toggle(key)}
                    className="inline-flex items-center gap-1 rounded-full bg-accent px-2.5 py-0.5 font-mono text-xs text-white"
                  >
                    {key}
                    <X size={12} />
                  </button>
                ))}
              </div>
            )}

            <div>
              <h3 className="mb-2 text-sm font-medium text-secondary">{t.flags.depsPrereqs}</h3>
              <div className="mb-2 flex items-center gap-2 rounded-md border border-border-default bg-surface-2 px-2.5 py-1.5">
                <Search size={14} className="text-tertiary" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={t.loc.searchPh}
                  className="w-full bg-transparent text-sm text-primary outline-none placeholder:text-tertiary"
                />
              </div>
              {candidates.length === 0 ? (
                <div className="px-2 py-4 text-sm text-tertiary">{t.states.emptyTitle}</div>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {candidates.map((f) => (
                    <button
                      key={f.key}
                      onClick={() => toggle(f.key)}
                      className={cn(
                        'rounded-md border px-2 py-0.5 font-mono text-xs',
                        picked.has(f.key) ? 'border-accent bg-accent text-white' : 'border-border-default bg-surface-1 text-secondary hover:text-primary',
                      )}
                    >
                      {f.key}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 border-t border-border-subtle pt-4">
              <button
                onClick={save}
                disabled={busy}
                className="rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
              >
                {t.common.save}
              </button>
              {picked.size > 0 && (
                <button
                  onClick={() => setPicked(new Set())}
                  className="rounded-md border border-border-default bg-surface-2 px-4 py-1.5 text-sm text-secondary hover:text-primary"
                >
                  {t.flags.depsClear}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
